<template>
  <div
    class="input"
    :class="{
      error: vErrors.length > 0 || isError,
    }"
  >
    <div class="input__title" :class="{ 'to-top': isLabelToTop }">
      {{ title }}
      <div class="input__title-icon">
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="4.80512" cy="4.80512" r="3.80512" stroke="#67768A" />
          <line
            x1="0.5"
            y1="-0.5"
            x2="4.95548"
            y2="-0.5"
            transform="matrix(-0.707107 -0.707107 -0.707107 0.707107 10.5659 11.8331)"
            stroke="#67768A"
            stroke-linecap="round"
          />
        </svg>
      </div>
    </div>
    <input
      :type="type"
      class="input__el"
      :title="title"
      :disabled="disabled"
      v-model="text"
      @input="processInput"
      @focus="processFocus"
      @blur="processBlur"
      @keypress.enter="$emit('enter')"
      v-if="!mask"
      ref="inputEl"
      :placeholder="placeholder"
    />
    <input
      :type="type"
      class="input__el"
      :title="title"
      :disabled="disabled"
      v-model="text"
      @input="processInput"
      @focus="processFocus"
      @blur="processBlur"
      @keypress.enter="$emit('enter')"
      v-mask="mask"
      v-else
      ref="inputEl"
      :placeholder="placeholder"
    />
    <div class="input__hint" v-if="hint">
      {{ hint }}
    </div>
    <div class="input__errors" v-if="vErrors.length > 0">
      <div class="input__error">{{ vErrors[0].$message }}</div>
    </div>
  </div>
</template>

<script>
import { $on, $off, $once, $emit } from '../../../utils/gogocodeTransfer'
export default {
  name: 'SearchInput',
  model: {
    prop: 'value',
    event: 'update',
  },
  props: {
    title: {
      type: [String, Number],
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: null,
    },
    value: [String, Number],
    hint: {
      type: String,
      default: '',
    },
    closeButton: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    vErrors: {
      type: Array,
      default() {
        return []
      },
    },
    isError: {
      type: Boolean,
      default: false,
    },
    mask: {
      type: [String, Object, Function, Array],
      default: null,
    },
    canCopy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      text: '',
      isFocused: false,
    }
  },
  created() {
    this.text = this.value === 0 ? this.value : this.value || ''
  },
  mounted() {
		this.$emit('canFocus', this)
  },
  computed: {
    isLabelToTop: function () {
      return (this.text ? this.text.length > 0 : false) || this.isFocused
    },
  },
  watch: {
    value: function () {
      this.text = this.value
    },
    text: function () {
      this.text = this.text.replaceAll(
        /[^А-Яа-я0-9A-Za-z\-+_.*;'ІЇЩЄҐіїщєґ,$#"«»\[\]()%\\|?{}<>\s@\/]/gmu,
        ''
      )
    },
  },
  methods: {
    checkAnimation(e) {
      if (e.animationName === 'onAutoFillStart') {
        this.text = this.text + ' '
        this.autofilled = true
      } else if (e.animationName === 'onAutoFillCancel') {
        this.text = this.text + ' '
        this.autofilled = false
      }
    },

    processFocus() {
			this.$emit('textInputFocus')
      this.isFocused = true
    },
    processBlur() {
			this.$emit('textInputBlur')
      this.isFocused = false
    },
    processInput() {
			this.$emit('update:value', this.text)
    },
    copyToClipboard() {
      if (!navigator.clipboard) {
        return
      }

      navigator.clipboard.writeText(this.text).then(
        () => {
					this.$emit('copied')
        },
        (err) => {
          console.error('Async: Could not copy text: ', err)
        }
      )
    },
    focus() {
      this.$refs.inputEl.focus()
    },
  },
  emits: [
    'enter',
    'canFocus',
    'update',
    'textInputFocus',
    'textInputBlur',
    'copied',
    'update:value',
    'enter'
  ],
}
</script>

<style lang="scss" scoped>
.input {
  width: 100%;
  height: 100%;
  position: relative;
  &__title {
    font-weight: 400;
    font-size: 12px;
    line-height: 15px;
    color: #909cb0;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%);
    transition: transform 0.2s linear, top 0.2s linear, left 0.2s linear;
    pointer-events: none;

    &.to-top {
      padding: 2px 10px;
      top: 0;
      left: 15px;
      transform: translateY(-50%);
      color: #465a8b;
    }

    background-color: #fff;
    border-radius: 6px;

    &-icon {
      width: 12px;
      height: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 5px;

      svg {
        max-width: 100%;
        max-height: 100%;
      }
    }
  }

  &__el {
    width: 100%;
    border: 1px solid transparent;
    background: #ffffff;
    border-radius: 8px;
    padding: 13px 15px 13px 21px;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #585f6d;
    height: 100%;
    outline: none;

    &:focus,
    &:active {
      border-color: #64748B;
    }

    &:disabled {
      border-color: transparent;
      color: #d0d9ea;
    }
  }

  &__hint {
    margin-top: 4px;
    font-weight: 500;
    font-size: 10px;
    line-height: 14px;
    color: #b9c4d8;

    &:empty {
      display: none;
    }
  }
}
</style>

<style>
:-webkit-autofill {
  animation-name: onAutoFillStart;
}
:not(:-webkit-autofill) {
  animation-name: onAutoFillCancel;
}
@keyframes onAutoFillStart {
  from {
  }
  to {
  }
}
@keyframes onAutoFillCancel {
  from {
  }
  to {
  }
}
</style>
